import React, { useEffect, useMemo, useState } from 'react';
import { createContract, ContractAdminDto } from '../../../services/contractsService';
import { FetchJobResult, ProbeResult, Suggestion, ValidateResult } from '../../../services/refinitivService';
import { MONTH_NAMES, useRefinitivJob } from './useRefinitivJob';
import { FetchJobSummary, JobStatusLine, ValidateTable } from './JobStatusLine';

/**
 * "Add from Refinitiv": type a RIC root (or a description, with Gemini), the VM worker reads the chain
 * 0#ROOT: and the API derives the settings; the user only names it and picks a unit.
 * docs/market-data/refinitiv-on-demand.md
 */
const CURRENCIES = ['USD', 'EUR', 'GBP', 'ILS', 'CNY', 'JPY'];
const QUOTE_UNITS = [
  { value: 'metricTon', label: 'Metric Ton (MT)' },
  { value: 'shortTon', label: 'Short Ton' },
  { value: 'grossTon', label: 'Gross Ton' },
  { value: 'cwt', label: 'Hundredweight (cwt)' },
  { value: 'lbs', label: 'Pounds (lbs)' },
  { value: 'libra', label: 'Libra' },
  { value: 'bbl', label: 'Barrel (bbl)' },
  { value: 'index', label: 'Index Points' },
  { value: 'rate', label: 'Rate (FX)' },
];
const ROOT_RE = /^[A-Za-z0-9.=^_-]{1,30}$/;
const MINOR_TO_MAJOR: Record<string, string> = { USc: 'USD', GBp: 'GBP' };

type Step = 'search' | 'review' | 'saved';

interface Props {
  existingCodes: string[];
  workerOnline?: boolean;
  llm?: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const AddFromRefinitivWizard: React.FC<Props> = ({ existingCodes, workerOnline, llm, onClose, onCreated }) => {
  const [step, setStep] = useState<Step>('search');
  const [input, setInput] = useState('');
  const probe = useRefinitivJob<ProbeResult>();
  const check = useRefinitivJob<ValidateResult>();
  const history = useRefinitivJob<FetchJobResult>();
  const [pick, setPick] = useState(0);
  const [saved, setSaved] = useState<ContractAdminDto | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState('2024-01-01');
  const [form, setForm] = useState({ code: '', name: '', currency: 'USD', quoteUnit: 'metricTon', unitMultiplier: 1, sourceUnit: '' });
  const [showSettings, setShowSettings] = useState(false);

  const raw = input.trim().replace(/^0#|:$/g, '');
  const isRoot = ROOT_RE.test(raw);
  const result = probe.job?.status === 'done' ? probe.job.result : null;
  const options = useMemo(() => (result?.suggestions ?? []).map((s, i) => ({ s, i })).filter(({ s }) => s.ok), [result]);
  const chosen: Suggestion | null = options.find((o) => o.i === pick)?.s ?? options[0]?.s ?? null;

  // Pre-fill the form from the chosen suggestion (+ LLM reading for the first one)
  useEffect(() => {
    if (!chosen || !result) return;
    const ai = result.suggestions.indexOf(chosen) === result.suggestions.findIndex((s) => s.ok) ? result.llm : null;
    const cur = chosen.currency ?? 'USD';
    setForm({
      code: chosen.root.toUpperCase(),
      name: ai?.commodity || titleCase(chosen.name) || chosen.root,
      currency: CURRENCIES.includes(cur) ? cur : MINOR_TO_MAJOR[cur] ?? 'USD',
      quoteUnit: ai?.quoteUnit ?? 'metricTon',
      unitMultiplier: ai?.unitMultiplier ?? 1,
      sourceUnit: ai?.sourceUnit ?? (MINOR_TO_MAJOR[cur] ? cur : ''),
    });
    setStep('review');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen?.root, result]);

  const runProbe = () => probe.start('probe', isRoot ? { roots: [raw] } : { query: input.trim() });

  const save = async () => {
    if (!chosen?.ricMethod) return;
    if (existingCodes.includes(form.code)) {
      setSaveError(`Code ${form.code} already exists`);
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const c = await createContract({
        code: form.code.trim(),
        name: form.name.trim(),
        type: 'material',
        currency: form.currency,
        quoteUnit: form.quoteUnit,
        unitMultiplier: form.unitMultiplier,
        sourceUnit: form.sourceUnit || null,
        provider: 'refinitiv',
        symbol: chosen.root,
        baseSymbol: chosen.root,
        ricMethod: chosen.ricMethod,
        ricYearStyle: chosen.ricMethod === 'monthly' ? chosen.ricYearStyle : null,
        tradingCalendar: chosen.ricMethod === 'monthly' ? chosen.tradingCalendar : null,
        spotSuffix: chosen.ricMethod === 'monthly' ? chosen.spotSuffix : null,
        futuresDepth: chosen.futuresDepth ?? 12,
        active: true,
      });
      setSaved(c);
      setStep('saved');
      onCreated();
      check.start('validate', { contractId: Number(c.id) });
    } catch (e) {
      setSaveError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-end z-50">
      <div className="bg-white h-full w-full max-w-3xl overflow-y-auto shadow-xl">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
          <div>
            <h2 className="text-xl font-bold">Add from Refinitiv</h2>
            <p className="text-xs text-gray-500">
              Worker: {workerOnline ? <span className="text-green-700">online</span> : <span className="text-amber-700">offline — jobs wait until the VM is up</span>}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="p-6 space-y-6">
          {/* 1. Search */}
          <section>
            <h3 className="text-sm font-semibold text-gray-700 mb-2 uppercase">1. What to add</h3>
            <div className="flex gap-2">
              <input
                autoFocus
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && input.trim() && !probe.busy && runProbe()}
                placeholder={llm ? 'RIC root (e.g. SRB, C, LCO) or a description (e.g. "Shanghai rebar")' : 'RIC root, e.g. SRB, C, LCO'}
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <button
                onClick={runProbe}
                disabled={!input.trim() || probe.busy || (!isRoot && !llm)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                Check with Refinitiv
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              The root is the part between <code>0#</code> and <code>:</code> of the futures chain in Workspace (search the commodity, pick the &ldquo;Commodity Future Chain&rdquo; result).
              {!isRoot && input.trim() && !llm && <span className="text-amber-700"> Searching by description needs the Gemini key on the API.</span>}
            </p>
            <div className="mt-2">
              <JobStatusLine job={probe.job} error={probe.error} workerOnline={workerOnline} />
              {result && !options.length && (
                <div className="text-sm text-red-700 mt-2">
                  Nothing usable found on Refinitiv.
                  <ul className="list-disc ml-5 text-xs text-gray-600">
                    {result.suggestions.map((s) => <li key={s.root}><b>{s.root}</b>: {s.warnings.join(' ')}</li>)}
                  </ul>
                </div>
              )}
            </div>
          </section>

          {/* 2. Review */}
          {chosen && step !== 'search' && (
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">2. What Refinitiv has</h3>

              {options.length > 1 && (
                <div className="flex flex-wrap gap-2">
                  {options.map(({ s, i }) => (
                    <button key={s.root} disabled={step === 'saved'} onClick={() => setPick(i)}
                      className={`px-3 py-1 text-sm rounded border ${chosen === s ? 'bg-blue-600 text-white border-blue-600' : 'border-gray-300'}`}>
                      {s.root} · {s.name ?? '?'} · {s.exchange ?? ''}
                    </button>
                  ))}
                </div>
              )}

              {result?.llm && result.suggestions.indexOf(chosen) === result.suggestions.findIndex((s) => s.ok) && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                  <p className="text-purple-900"><b>{result.llm.commodity}</b> — {result.llm.exchangeName}. {result.llm.summary}</p>
                  {result.llm.cautions.length > 0 && (
                    <ul className="list-disc ml-5 mt-1 text-purple-800 text-xs">{result.llm.cautions.map((c) => <li key={c}>{c}</li>)}</ul>
                  )}
                  <p className="text-[11px] text-purple-500 mt-1">AI reading (Gemini) — a suggestion, check it.</p>
                </div>
              )}

              <div className="grid grid-cols-3 gap-3 text-sm">
                <Fact label="Exchange" value={chosen.exchange} />
                <Fact label="Currency (Refinitiv)" value={chosen.currency} />
                <Fact label="Live contracts" value={String(chosen.contracts.length)} />
              </div>

              {chosen.warnings.length > 0 && (
                <ul className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 list-disc ml-0 pl-6">
                  {chosen.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              )}

              {chosen.contracts.length > 0 && (
                <table className="w-full text-xs">
                  <thead className="text-gray-500"><tr><th className="text-left">RIC</th><th className="text-left">Month</th><th className="text-left">Expiry</th><th className="text-right">Last</th></tr></thead>
                  <tbody>
                    {chosen.contracts.slice(0, 14).map((c) => (
                      <tr key={c.ric}><td className="font-mono py-0.5">{c.ric}</td><td>{c.maturityMonth.slice(0, 7)}</td><td>{c.expiry ?? ''}</td><td className="text-right">{c.last ?? ''}</td></tr>
                    ))}
                  </tbody>
                </table>
              )}

              <h3 className="text-sm font-semibold text-gray-700 uppercase pt-2">3. Name it</h3>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Code *"><input value={form.code} disabled={step === 'saved'} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className={inputCls} /></Field>
                <Field label="Name *"><input value={form.name} disabled={step === 'saved'} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} /></Field>
                <Field label="Currency *">
                  <select value={form.currency} disabled={step === 'saved'} onChange={(e) => setForm({ ...form, currency: e.target.value })} className={inputCls}>
                    {CURRENCIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <Field label="Quote unit *">
                  <select value={form.quoteUnit} disabled={step === 'saved'} onChange={(e) => setForm({ ...form, quoteUnit: e.target.value })} className={inputCls}>
                    {QUOTE_UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </Field>
              </div>

              <button type="button" onClick={() => setShowSettings(!showSettings)} className="text-xs text-blue-600">
                {showSettings ? '▾' : '▸'} Settings derived from Refinitiv
              </button>
              {showSettings && (
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs bg-gray-50 rounded p-3">
                  <Fact label="RIC method" value={chosen.ricMethod} />
                  <Fact label="Year style" value={chosen.ricYearStyle} />
                  <Fact label="Traded months" value={chosen.tradingCalendar?.map((m) => MONTH_NAMES[m]).join(' ') ?? '—'} />
                  <Fact label="Depth (months ahead)" value={String(chosen.futuresDepth ?? '')} />
                  <Fact label="Spot RIC" value={chosen.spotSuffix ? `${chosen.root}${chosen.spotSuffix}` : 'from the contract'} />
                  <Fact label="Unit multiplier / source unit" value={`${form.unitMultiplier} / ${form.sourceUnit || '—'}`} />
                  <p className="col-span-2 text-gray-500 mt-1">Change these after saving in the contract&apos;s edit form if needed.</p>
                </div>
              )}

              {step === 'review' && (
                <div className="flex items-center gap-3">
                  <button onClick={save} disabled={saving || !form.code.trim() || !form.name.trim()}
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50" style={{ backgroundColor: '#9333ea' }}>
                    {saving ? 'Saving…' : 'Create contract'}
                  </button>
                  {saveError && <span className="text-sm text-red-600">{saveError}</span>}
                </div>
              )}
            </section>
          )}

          {/* 4. After save: check + history */}
          {step === 'saved' && saved && (
            <section className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase">4. Saved: {saved.code}</h3>
              <div>
                <JobStatusLine job={check.job} error={check.error} workerOnline={workerOnline} />
                {check.job?.status === 'done' && check.job.result && <ValidateTable result={check.job.result} />}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-gray-700">Load history from</span>
                <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-2 py-1 border border-gray-300 rounded text-sm" />
                <button disabled={history.busy || history.job?.status === 'done'}
                  onClick={() => history.start('backfill', { contractId: Number(saved.id), startDate })}
                  className="px-3 py-1.5 text-sm rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                  Load history
                </button>
                <span className="text-xs text-gray-500">Otherwise prices start with the next daily fetch (07:00 Israel time).</span>
              </div>
              <JobStatusLine job={history.job} error={history.error} workerOnline={workerOnline} />
              {history.job?.status === 'done' && history.job.result && <FetchJobSummary result={history.job.result} />}
              <div className="pt-2">
                <button onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50">Close</button>
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

const inputCls = 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100';
const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <label className="block"><span className="block text-sm font-medium text-gray-700 mb-1">{label}</span>{children}</label>
);
const Fact: React.FC<{ label: string; value: string | null | undefined }> = ({ label, value }) => (
  <div><span className="text-gray-500">{label}: </span><span className="text-gray-900 font-medium">{value ?? '—'}</span></div>
);
const titleCase = (s: string | null) => (s ? s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()).replace(/\b(Shfe|Lme|Cbot|Ice|Cme|Dce|Czce|Nymex|Comex)\b/g, (m) => m.toUpperCase()) : null);

export default AddFromRefinitivWizard;
